import axios from "axios";
import { createMessage, createGenericErrorAlert } from "./messages";
import { getPeopleByRequestCount } from "./people";

import { RESPOND_TO_REG_COMPANY_INVITATION, EDIT_USER, UPLOAD_USER_ICON, USER_LOAD_CANCELED, USER_LOADING, USER_LOADED, AUTH_ERROR, LOGIN_SUCCESS, VERIFY_SUCCESS, LOGIN_FAIL, LOGOUT_SUCCESS, LOGOUT_FAIL, REGISTER_SUCCESS, REGISTER_FAIL, CLEAR_REGISTRATION, ACTIVATE_SUCCESS, ACTIVATE_FAIL, EXTERNAL_ACTION, GET_INVITE_DETAIL } from "./types";


// LOGIN USER
export const login = (email, password) => (dispatch) => {
    const config = {
        headers: {
            "Content-Type": "application/json"
        }
    };
    // Grab the request Body
    const body = JSON.stringify({ email, password });
    return axios
        .post("/v0/api/auth/login", body, config)
        .then((res) => {
            if (res.data.error) {
                dispatch(createMessage({ error: res.data.error }))
            } else {
                dispatch({
                    type: LOGIN_SUCCESS,
                    payload: res.data
                });
            }
        })
        .catch((err) => {
            if (err.response.data.non_field_errors) {
                dispatch(createMessage({ error: err.response.data.non_field_errors[0] }))
            } else {
                dispatch(createGenericErrorAlert({...err.response.data, ...err.response.status}));
            }
            dispatch({
                type: LOGIN_FAIL
            });
        });
};

// EXCHANGE A ONE-TIME OAUTH CODE FOR A LOGIN (GitHub #579) -- landed on by
// OAuthCallback.js after the provider-redirect dance; dispatches the exact
// same LOGIN_SUCCESS type as password login, since the response shape is
// identical (authentication/utils.py's build_authenticated_response).
export const exchangeOAuthCode = (code) => (dispatch) => {
    const config = {
        headers: {
            "Content-Type": "application/json"
        }
    };
    const body = JSON.stringify({ code });
    return axios
        .post("/v0/api/auth/oauth-exchange", body, config)
        .then((res) => {
            if (res.data.error) {
                dispatch(createMessage({ error: res.data.error }));
                return false;
            }
            dispatch({
                type: LOGIN_SUCCESS,
                payload: res.data
            });
            return true;
        })
        .catch((err) => {
            dispatch(createGenericErrorAlert({...err.response.data, ...err.response.status}));
            return false;
        });
};

// LOOK UP A COMPANY INVITE (public -- used by the registration page before signup)
export const getCompanyInviteDetail = (inviteUuid) => (dispatch) => {
    axios
        .get(`/v0/api/auth/company-invite-detail/${inviteUuid}/`)
        .then((res) => {
            dispatch({
                type: GET_INVITE_DETAIL,
                payload: res.data
            });
        })
        .catch((err) => dispatch({
            type: GET_INVITE_DETAIL,
            payload: {error: "This invitation is no longer valid"}
        }));
};

// REGISTER USER
export const register = (user) => (dispatch) => {
    const config = {
        headers: {
            "Content-Type": "application/json"
        }
    };
    // Grab the request Body
    const body = JSON.stringify(user);
    axios
        .post("/v0/api/auth/register", body, config)
        .then((res) => {
            // Look for errors returned by the API in regards to failed registration
            if (res.data.error) {
                dispatch(createMessage({...res.data, status: res.status}));
                dispatch({
                    type: REGISTER_FAIL,
                    payload: res.data
                });
            }
            else {
                dispatch({
                    type: REGISTER_SUCCESS,
                    payload: res.data
                });
            }
        })
        .catch((err) => {
            if (err.response.data.non_field_errors) {
                dispatch(createMessage({ error: err.response.data.non_field_errors[0] }))
            } else {
                dispatch(createGenericErrorAlert({...err.response.data, ...err.response.status}));
            }
            dispatch({
                type: REGISTER_FAIL,
                payload: res.data
            });
        });
};
// CLEAR REGISTRATION
export const clearRegistration = () => (dispatch) => {
    dispatch({
        type: CLEAR_REGISTRATION,
        payload: null
    });
}



// ACTIVATE ACCOUNT
export const activateAccount = (uidb, token) => (dispatch) => {
    axios
        .get(`v0/api/auth/activate/${uidb}/${token}`)
        .then((res) => {
            // Look for errors returned by the API in regards to failed activation
            if (res.data.success) {
                if (res.data.success === "email_verified") {
                    dispatch({
                        type: VERIFY_SUCCESS,
                        payload: res.data
                    })
                } else {
                    dispatch({
                        type: ACTIVATE_SUCCESS,
                        payload: res.data
                    });
                    dispatch(createMessage({ success: "Successfully activated account." }));
                }
            } else if (res.data.error) {
                dispatch(createGenericErrorAlert({...err.response.data, ...err.response.status}));
                dispatch({
                    type: ACTIVATE_FAIL
                });
                dispatch(createMessage({ error: res.data.error }));
            }
        })
        .catch((err) => {
            if (err.response) {
                if (err.response.data?.non_field_errors) {
                    dispatch(createMessage({ error: err.response.data.non_field_errors[0] }))
                } else {
                    dispatch(createGenericErrorAlert({...err.response.data, ...err.response.status}));
                }
            }
            dispatch({
                type: ACTIVATE_FAIL
            });
        });
};

// REQUEST PASSWORD RESET
export const requestPasswordReset = (email) => (dispatch) => {
    const config = {
        headers: {
            "Content-Type": "application/json"
        }
    };
    const body = JSON.stringify({ email });
    return axios
        .post("/v0/api/auth/password-reset", body, config)
        .then((res) => {
            dispatch(createMessage({ success: res.data.success }));
            return true;
        })
        .catch((err) => {
            dispatch(createGenericErrorAlert({...err.response.data, ...err.response.status}));
            return false;
        });
};

// CONFIRM PASSWORD RESET
export const confirmPasswordReset = (uidb64, token, password) => (dispatch) => {
    const config = {
        headers: {
            "Content-Type": "application/json"
        }
    };
    const body = JSON.stringify({ password });
    return axios
        .post(`/v0/api/auth/password-reset-confirm/${uidb64}/${token}/`, body, config)
        .then((res) => {
            if (res.data.error) {
                dispatch(createMessage({ error: res.data.error }));
                return false;
            }
            dispatch(createMessage({ success: "Your password has been reset. You can now log in." }));
            return true;
        })
        .catch((err) => {
            dispatch(createGenericErrorAlert({...err.response.data, ...err.response.status}));
            return false;
        });
};

// LOGOUT USER
export const logout = () => (dispatch, getState) => {
    console.log("triggering logout from action file")
    axios
        .post("/v0/api/auth/logout", null, tokenConfig(getState))
        .then((res) => {
            if (res.data.error) {
                dispatch(createMessage({ error: res.data.error }))
            } else {
                dispatch({
                    type: LOGOUT_SUCCESS
                });
            }
        })
        .catch((err) => {
            if (err.response.status === 401) {
                dispatch({
                    type: LOGOUT_SUCCESS
                });
            } else {
                dispatch({
                    type: LOGOUT_FAIL
                });
            }
            // dispatch(createGenericErrorAlert({...err.response.data, ...err.response.status}));
        });
};

// CHECK TOKEN & LOAD USER
export const loadUser = () => (dispatch, getState) => {
    // Grab the request configuration with token
    var config = tokenConfig(getState);
    if (config) {
        // User Loading
        dispatch({ type: USER_LOADING });
        axios
            .get("v0/api/auth/user", tokenConfig(getState))
            .then((res) => {
                if (res.data.error) {
                    dispatch(createMessage({ error: res.data.error }))
                } else {
                    let verifying = localStorage.getItem("verification_ongoing");
                    if (verifying) {
                        localStorage.removeItem("verification_ongoing");
                        dispatch(logout({}));
                    } else {
                        dispatch({
                            type: USER_LOADED,
                            payload: res.data
                        });
                    }
                }
                // If an account is being activated/verified, and a previous user is logged in, log them out
            })
            .catch((err) => {
                if (err.response.data.non_field_errors) {
                    dispatch(createMessage({ error: err.response.data.non_field_errors[0] }))
                } else {
                    dispatch(createGenericErrorAlert({...err.response.data, ...err.response.status}));
                }
                dispatch({
                    type: AUTH_ERROR
                });
            });
    }
};

// EDIT USER
export const editUser = (user) => (dispatch, getState) => {
    axios
        .put(`/v0/api/auth/user-edit`, user, tokenConfig(getState))
        .then((res) => {
            if (res.data.error) {
                dispatch(createMessage({ error: res.data.error }))
            } else {
                dispatch({
                    type: EDIT_USER,
                    payload: res.data,
                });
                dispatch(createMessage({ success: "User profile updated." }));
            }
        })
        .catch((err) => {
            if (err.response.data.non_field_errors) {
                dispatch(createMessage({ error: err.response.data.non_field_errors[0] }))
            } else {
                dispatch(createGenericErrorAlert({...err.response.data, ...err.response.status}));
            }
        });
};

// UPLOAD USER ICON
export const uploadUserIcon = (file_name, formData) => (dispatch, getState) => {
    axios
        .post(`/v0/api/core/upload/user-icon/${file_name}/`, formData, tokenConfigUpload(getState))
        .then((res) => {
            if (res.data.error) {
                dispatch(createMessage({ error: res.data.error }))
            } else {
                dispatch({
                    type: USER_LOADED,
                    payload: res.data
                });
            }
        })
        .catch((err) => dispatch(createGenericErrorAlert({...err.response.data, ...err.response.status})));
};

// Setup config with token - helper function
export const tokenConfig = (getState) => {
    // Get token from state
    const token = getState().auth.token;
    let config = null;
    // Once a token is returned, store it to the header config
    if (token) {
        config = {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Token ${token}`
            },
        };
    }
    return config;
};

export const tokenConfigUpload = (getState) => {
    // Get token from state
    const token = getState().auth.token;
    let config = null;
    // Once a token is returned, store it to the header config. Deliberately no
    // Content-Type here -- axios/the browser must generate multipart/form-data's
    // boundary parameter itself for a FormData body; a hardcoded header (with no
    // boundary, or overriding axios's auto-detection with the wrong media type
    // entirely) breaks server-side multipart parsing, which is exactly what was
    // happening everywhere this should have been used instead of tokenConfig.
    if (token) {
        config = {
            headers: {
                "Authorization": `Token ${token}`
            },
        };
    }
    return config;
};

// ACCEPT OR REJECT REGISTRATION COMPANY INVITATION
export const respondToRegCompanyInvitation = (id, status, roles) => (dispatch, getState) => {
    axios
        .put(`/v0/api/auth/update-company-invite/${id}/${status}/`, roles, tokenConfig(getState))
        .then((res) => {
            if (res.data.error) {
                dispatch(createMessage({ error: res.data.error }))
            } else {
                dispatch({
                    type: RESPOND_TO_REG_COMPANY_INVITATION,
                    payload: id,
                });
                dispatch(getPeopleByRequestCount({}));
                if (status === "accept") {
                    dispatch(createMessage({ success: "Request approved." }));
                } else if (status === "reject") {
                    dispatch(createMessage({ success: "Request rejected." }));
                }
            }
        })
        .catch((err) => console.log(err));
    //   .catch((err) => dispatch(createGenericErrorAlert({...err.response.data, ...err.response.status})));
};

// RESPOND TO INVOICE VIA EXTERNAL EMAIL
export const takeExternalAction = (uidb, token, response) => (dispatch) => {
    axios
        .get(`v0/api/auth/ext-action/${uidb}/${token}/${response}`)
        .then((res) => {
            // Look for errors returned by the API in regards to failed activation
            dispatch({
                type: EXTERNAL_ACTION,
                payload: res.data
            })
        })
        .catch((err) => {
            dispatch(createMessage({ error: "An error occurred. Refresh and try again" }));
        });
};

// localhost:8000/v0/api/auth/ext-action/NGU4MDY1ZjAtNTAyMS00Nzk1LThmZmEtODU5NTIwZGFmYjQ5/aukrrv-f1ff4029cb3d85aee05f90d43dd33715/paid